/**
 * WS-HF F1 — the latch that stops one cloud job being submitted to Higgsfield twice.
 *
 * `hfMcpGenerate` **spends the operator's credits**. Like every HTTP call it can fail with the
 * outcome unknown: the request left the machine, the vendor accepted it and started a job, and
 * the reply was lost to a dropped connection, a closed laptop or a timeout. A plain retry then
 * generates — and bills — the same image a second time. The vendor's tools carry no
 * idempotency key of their own, so the client has to hold one.
 *
 * The latch is a tiny record per CLOUD job id:
 *
 * | state | means | what a retry does |
 * |---|---|---|
 * | *(absent)* | nothing has been sent | submit |
 * | `submitting` | a submission was started and its outcome is **unknown** | refuse, and say so |
 * | `submitted` | the vendor's job ids are known | resume waiting on those ids — never resubmit |
 *
 * It lives in memory AND in `localStorage`, so it survives a reload and a crash — which are
 * precisely the cases where the outcome was unknown. `localStorage` can throw (private mode,
 * blocked site data), so every access is guarded and the in-memory map is the source of truth
 * within a session.
 *
 * The `submitting` state is deliberately a DEAD END rather than an automatic recovery. This
 * client cannot list "the jobs I started in the last minute" — `transactions` shows spends
 * after the fact, not in-flight jobs — so the honest answer is to stop and tell the operator
 * to look at their Higgsfield account. Guessing costs real money.
 */

const PREFIX = "genz.hf.submission.";

/** How long a `submitting` record blocks a retry. After this the vendor's job is certainly
 *  finished (or lost) and a fresh submission is the lesser evil; 24 h is far beyond any
 *  generation, so within the window the answer is always "go and look". */
export const LATCH_TTL_MS = 24 * 60 * 60 * 1000;

/** Shown when a submission's outcome is unknown. The operator must check before spending
 *  again — this app cannot tell them whether the first attempt billed them. */
export const UNKNOWN_SUBMISSION_MESSAGE =
  "submission outcome unknown — check the Higgsfield account";

export interface SubmissionRecord {
  state: "submitting" | "submitted";
  /** Client-generated idempotency key for this attempt, carried into the vendor params and
   *  the operator log so the two can be matched up by hand. */
  key: string;
  /** The vendor's job ids, once they are known. */
  jobIds?: string[];
  /** Epoch ms of the attempt. */
  at: number;
}

const memory = new Map<string, SubmissionRecord>();

function store(): Storage | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

/** A key that is unique per attempt. `crypto.randomUUID` where it exists; otherwise time plus
 *  randomness, which is enough — this is a correlation id, not a secret. */
export function newIdempotencyKey(): string {
  try {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** The record for a cloud job, or `undefined` when there is none (or it has aged out). */
export function readLatch(cloudJobId: string): SubmissionRecord | undefined {
  let rec = memory.get(cloudJobId);
  if (!rec) {
    try {
      const raw = store()?.getItem(PREFIX + cloudJobId);
      if (raw) rec = JSON.parse(raw) as SubmissionRecord;
    } catch {
      return undefined;
    }
  }
  if (!rec || typeof rec.at !== "number") return undefined;
  if (Date.now() - rec.at > LATCH_TTL_MS) {
    clearLatch(cloudJobId);
    return undefined;
  }
  memory.set(cloudJobId, rec);
  return rec;
}

function write(cloudJobId: string, rec: SubmissionRecord): void {
  memory.set(cloudJobId, rec);
  try {
    store()?.setItem(PREFIX + cloudJobId, JSON.stringify(rec));
  } catch {
    /* in-memory still holds it for this session */
  }
}

/** Written BEFORE the submission leaves. If the process dies here, the record is what says
 *  "we do not know". */
export function markSubmitting(cloudJobId: string, key: string): void {
  write(cloudJobId, { state: "submitting", key, at: Date.now() });
}

/** Written the moment the vendor's job ids are known — the point at which a retry can safely
 *  resume rather than resubmit. */
export function markSubmitted(cloudJobId: string, key: string, jobIds: string[]): void {
  write(cloudJobId, { state: "submitted", key, jobIds, at: Date.now() });
}

/** Drop the record. Called once the job reached a TERMINAL state, where there is nothing left
 *  to protect: the money was spent (or refused) and the outcome is recorded on the cloud job. */
export function clearLatch(cloudJobId: string): void {
  memory.delete(cloudJobId);
  try {
    store()?.removeItem(PREFIX + cloudJobId);
  } catch {
    /* ignore */
  }
}

/** Tests only — drop every record this module holds in memory. */
export function resetLatchesForTest(): void {
  memory.clear();
}
